/**
 * Synthetic-capture detection (C-614).
 *
 * WHY THIS EXISTS
 * ---------------
 * The fleet's post-deploy "Capture page snapshots" step drives real Chromium
 * against the LIVE production URL of the site it just deployed. The site's
 * telemetry boots exactly as it would for a visitor, so every page the capture
 * pass walked was recorded as a production pageView — during our own deploy run.
 *
 * Measured on coron8 (the smallest-traffic fleet site) over the 8 days to
 * 2026-08-11: 49 synthetic pageViews against 14 organic — 78% of the entire
 * store. All 49 landed on 08-11 across 5 deploys, against 3 real visits that
 * day, so the owner's chart read 52 where the truth was 3. Every site carrying
 * the C-589 telemetry sink plus a snapshot CI step has this.
 *
 * WHY SUPPRESSION AND NOT TAGGING
 * -------------------------------
 * The alternative was to tag the traffic with a synthetic-source dimension and
 * exclude it in every read path. That is precisely the C-430 preview/localhost
 * blend, which is a recorded UNSOLVED residual rather than a pattern worth
 * copying: it obliges every present and future consumer — the portal analytics
 * query, the MCP `analytics_summary` tool, ad-hoc KQL an owner runs — to
 * remember an exclusion, and a consumer that forgets reports the inflated
 * number with no signal that it did. Declining at the boundary keeps the
 * denominator honest in all of them at once, and matches the choice already
 * made for previews in C-601 (dev/preview telemetry points at the dev
 * component, not the production store).
 *
 * WHY THESE MARKERS
 * -----------------
 * `dcs-hide-ribbon` is not invented here. Every DCS capture tool already
 * appends it to suppress the preview ribbon in screenshots — the shared
 * `packages/cli/src/commands/capture-snapshots.ts`, each site's own
 * `scripts/capture-snapshots.*`, and the portfolio/CWV tooling that
 * `@duffcloudservices/agent-widget`'s suppression module documents. It is
 * consumed by `PreviewRibbon.vue`. Crucially, NO human-navigable link on a
 * fleet site produces it, so the fleet already emits an accurate
 * "this is a machine" marker everywhere and only the honoring half was missing.
 *
 * `dcs-no-telemetry` is the explicit, self-documenting name for the same
 * intent, so future tooling need not overload a presentational parameter. Its
 * second job is to make the fix self-validating: if a site's gate ever fails,
 * the rows that get recorded carry the parameter and say so.
 */

/** Query parameters that mark a document as an automated capture, not a visit. */
export const SYNTHETIC_CAPTURE_PARAMS = ['dcs-hide-ribbon', 'dcs-no-telemetry'] as const

/** Reason {@link syntheticCaptureDetected} returned true, for logging and skip reporting. */
export type SyntheticCaptureMarker = (typeof SYNTHETIC_CAPTURE_PARAMS)[number]

/**
 * The marker present on this document, or `null` when this looks like a real visit.
 *
 * Reads `window.location.search` on every call rather than caching: a site that
 * calls this after a client-side route change must see the CURRENT URL, and a
 * cached first-load verdict would either leak suppression into a real user's
 * later navigation or miss a capture pass that navigated in-app.
 *
 * Returns `null` on the server — server rendering emits no browser telemetry,
 * and answering "synthetic" there would be a claim this function cannot support.
 */
export function syntheticCaptureMarker(): SyntheticCaptureMarker | null {
  if (typeof window === 'undefined') return null

  try {
    const params = new URLSearchParams(window.location.search)
    return SYNTHETIC_CAPTURE_PARAMS.find((param) => params.has(param)) ?? null
  } catch {
    // A malformed query string must never break a telemetry boot. Fail OPEN
    // (treat it as a real visit) so a parser edge case can only ever over-count,
    // never silently blind the owner's report.
    return null
  }
}

/**
 * Whether this document is an automated capture rather than a visit worth counting.
 *
 * Call this BEFORE loading an analytics SDK, so a capture pass never opens a
 * beacon at all:
 *
 * ```ts
 * if (syntheticCaptureDetected()) return false
 * const { ApplicationInsights } = await import('@microsoft/applicationinsights-web')
 * ```
 */
export function syntheticCaptureDetected(): boolean {
  return syntheticCaptureMarker() !== null
}
